import CheckIcon from '@mui/icons-material/Check'
import { Box, FormHelperText } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import { useEffect, useMemo, useState } from 'react'
import type { Control, UseFormSetValue } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'

import eyedrop from '@/assets/images/enamelEyeDrop.svg'
import Button from '@/components/Button'
import SnackbarTooltip from '@/components/SnackbarTooltip'
import Title from '@/components/Title'
import type { RootState } from '@/store'
import { setSelectedPickerKey } from '@/store/allInOne'
import type { REGION } from '@/utils/constants'

import EditInputField from './EditInputField'

interface ColorSelectionProps {
  name: string
  control: Control<FormData>
  error?: boolean
  title: REGION
  clearErrors: (name?: keyof FormData | (keyof FormData)[]) => void
  setValue: UseFormSetValue<FormData>
  containerRef?: React.RefObject<HTMLDivElement>
}

const ColorSelection = ({
  name,
  control,
  error = false,
  title,
  clearErrors,
  setValue,
  containerRef,
}: ColorSelectionProps) => {
  const { t } = useTranslation()

  const [snackbarOpen, setSnackbarOpen] = useState(false)

  const dispatch = useDispatch()
  const theme = useTheme()

  const data = useSelector((state: RootState) => state.allInOne.form[name])
  const shouldShowError = useMemo(() => {
    return error && Array.isArray(data)
      ? data?.filter(Boolean)?.length === 0
      : !data
  }, [error, data])

  useEffect(() => {
    if (!shouldShowError) {
      clearErrors(name)
    }
  }, [shouldShowError])

  const selectedPickerKey = useSelector(
    (state: RootState) => state.allInOne.selectedPickerKey,
  )

  useEffect(() => {
    if (!selectedPickerKey) {
      setSnackbarOpen(false)
    }
  }, [selectedPickerKey])
  return (
    <Box>
      <Box display="flex" alignItems="center" gap="10px" mb={1}>
        <Title fontSize="16px" lineHeight="normal">
          {title}
        </Title>
        <Button
          sx={{
            alignSelf: 'flex-start',
            fontSize: '16px',
            padding: '4px 10px',
            cursor: 'pointer',
            lineHeight: 'normal',
            height: '30px',
            width: '36px',
            minWidth: '36px',
            borderRadius: '4px',
          }}
          variant="outlined"
          size="small"
          label={
            selectedPickerKey === name ? (
              <CheckIcon fontSize="small" />
            ) : (
              <img src={eyedrop} alt="Select" width={16} height={16} />
            )
          }
          textTransform="uppercase"
          onClick={() => {
            dispatch(setSelectedPickerKey(name))
            setSnackbarOpen(true)
          }}
        />
      </Box>
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
        {[0, 1, 2].map((index) => (
          <EditInputField
            key={index}
            name={name}
            index={index}
            data={data}
            setValue={setValue}
            shouldShowError={shouldShowError}
            control={control}
          />
        ))}
      </Box>
      {shouldShowError && (
        <FormHelperText sx={{ color: theme.palette.error.main, pl: 1.5 }}>
          {`${t(`colors.${name}`)} ${t('colors.validation.isRequired')}`}
        </FormHelperText>
      )}

      <SnackbarTooltip
        containerRef={containerRef}
        message="Click on the spot you want to measure."
        open={snackbarOpen}
        onClose={() => setSnackbarOpen(false)}
        placement="top"
        offset={[0, -100]}
        maxWidth={500}
        autoHideDuration={6000}
      />
    </Box>
  )
}

export default ColorSelection
